import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState, useCallback } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DestructiveConfirm } from '@/components/ui/DestructiveConfirm'
import { SkeletonCard } from '@/components/ui/SkeletonLoader'
import { BRAND_MINT } from '@/lib/utils'
import {
  getAdminTransactionsApi,
  getAdminCyclesApi,
  forcePayCycleApi,
  type AdminTransaction,
  type AdminCycle,
} from '@/lib/api'
import {
  LoaderIcon,
  SearchIcon,
  FilterIcon,
  XIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BanknoteIcon,
  ActivityIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  HashIcon,
  DownloadIcon,
  RefreshCwIcon,
  AlertTriangleIcon,
  GlobeIcon,
  UsersIcon,
  BuildingIcon,
  ZapIcon,
} from 'lucide-react'

type Tab = 'transactions' | 'cycles'

function formatTimestamp(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function formatDate(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const CYCLE_STATUS_STYLES: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  paid: { label: 'Paid', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  overdue: { label: 'Overdue', color: 'bg-destructive/10 text-destructive' },
  cancelled: { label: 'Cancelled', color: 'bg-muted text-muted-foreground' },
}

const TXN_TYPE_STYLES: Record<string, { label: string; icon: React.ReactNode }> = {
  paid: { label: 'Payment', icon: <ArrowUpIcon className="size-3 text-emerald-500" /> },
  received: { label: 'Received', icon: <ArrowDownIcon className="size-3 text-blue-500" /> },
  payout: { label: 'Payout', icon: <ArrowDownIcon className="size-3 text-emerald-500" /> },
}

function defaultTxnType(type: string) {
  return TXN_TYPE_STYLES[type] ?? { label: type, icon: <ActivityIcon className="size-3 text-muted-foreground" /> }
}

export const Route = createFileRoute('/dashboard/admin/ledger')({
  component: () => {
    const [tab, setTab] = useState<Tab>('transactions')

    return (
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
            <div>
              <h1 className="text-2xl font-semibold">Platform ledger</h1>
              <p className="mt-1 text-sm text-muted-foreground">View all transactions and payment cycles across the platform.</p>
            </div>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="mb-6 flex gap-1 rounded-lg bg-muted p-1 w-fit">
          <button onClick={() => setTab('transactions')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${tab === 'transactions' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            Transactions
          </button>
          <button onClick={() => setTab('cycles')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${tab === 'cycles' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            Payment cycles
          </button>
        </div>

        {tab === 'transactions' ? <TransactionsView /> : <CyclesView />}
      </div>
    )
  },
})

function TransactionsView() {
  const [txns, setTxns] = useState<AdminTransaction[]>([])
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState<{ total_volume: string; total_volume_cents: number; completed_count: number } | null>(null)
  const [page, setPage] = useState(1)
  const [limit] = useState(50)
  const [loading, setLoading] = useState(true)

  // Filters
  const [orgIdFilter, setOrgIdFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  const totalPages = Math.max(1, Math.ceil(total / limit))

  const buildParams = useCallback(() => {
    const params: Record<string, string | number> = { page, limit }
    const orgId = parseInt(orgIdFilter, 10)
    if (!isNaN(orgId) && orgId > 0) params.org_id = orgId
    if (typeFilter) params.type = typeFilter
    if (fromDate) params.from_date = fromDate
    if (toDate) params.to_date = toDate
    return params
  }, [page, limit, orgIdFilter, typeFilter, fromDate, toDate])

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const res = await getAdminTransactionsApi(buildParams())
        if (!cancelled) { setTxns(res.data); setTotal(res.total); setSummary(res.summary ?? null) }
      } catch (err) {
        if (!cancelled) toast.error(err instanceof Error ? err.message : 'Failed to load transactions')
      } finally { if (!cancelled) setLoading(false) }
    }
    load()
    return () => { cancelled = true }
  }, [buildParams])

  const clearFilters = () => { setOrgIdFilter(''); setTypeFilter(''); setFromDate(''); setToDate(''); setPage(1) }
  const hasFilters = orgIdFilter || typeFilter || fromDate || toDate

  const exportCSV = () => {
    if (txns.length === 0) return
    const headers = ['ID', 'Type', 'From', 'To', 'Amount', 'Status', 'Org', 'Date']
    const rows = txns.map((t) => [
      t.id, t.type, t.from_name || '', t.to_name || '', t.amount, t.status, t.org_name || '', t.timestamp,
    ])
    const csv = [headers.join(','), ...rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `transactions_${new Date().toISOString().slice(0, 10)}.csv`
    a.click(); URL.revokeObjectURL(url)
  }

  if (loading && txns.length === 0) {
    return (
      <div>
        <SkeletonCard count={5} />
      </div>
    )
  }

  return (
    <div>
      {/* Summary + Export */}
      {summary && (
        <div className="mb-4 flex items-center gap-4">
          <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2">
            <BanknoteIcon className="size-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Volume:</span>
            <span className="text-sm font-semibold tabular-nums">${summary.total_volume}</span>
          </div>
          <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2">
            <CheckCircleIcon className="size-4 text-emerald-500" />
            <span className="text-xs text-muted-foreground">Completed:</span>
            <span className="text-sm font-semibold tabular-nums">{summary.completed_count}</span>
          </div>
          <Button variant="outline" size="sm" onClick={exportCSV} className="ml-auto gap-1.5">
            <DownloadIcon className="size-4" />Export CSV
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-1.5">
          <FilterIcon className="size-4" />{showFilters ? 'Hide filters' : 'Filters'}
          {hasFilters && <span className="flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">!</span>}
        </Button>
        {hasFilters && <Button variant="outline" size="sm" onClick={clearFilters} className="gap-1.5"><XIcon className="size-4" />Clear</Button>}
      </div>

      {showFilters && (
        <div className="mb-4 rounded-xl border border-border bg-card p-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Org ID</label>
              <input type="number" value={orgIdFilter} onChange={(e) => setOrgIdFilter(e.target.value)} placeholder="e.g. 5"
                className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Type</label>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary">
                <option value="">All types</option>
                <option value="paid">Payment</option>
                <option value="received">Received</option>
                <option value="payout">Payout</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">From</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">To</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary" />
            </div>
          </div>
        </div>
      )}

      <p className="mb-4 text-xs text-muted-foreground">{total} transaction{total !== 1 ? 's' : ''}{page > 1 && ` — page ${page} of ${totalPages}`}</p>

      {txns.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
          <BanknoteIcon className="size-10 text-muted-foreground/40" />
          <p className="text-sm font-medium text-foreground">No transactions found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {txns.map((txn) => {
            const typeInfo = defaultTxnType(txn.type)
            return (
              <div key={txn.id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-start gap-4">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">{typeInfo.icon}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-foreground">{typeInfo.label}</span>
                      <span className="text-muted-foreground/50">·</span>
                      <span className="font-medium tabular-nums">{txn.currency_symbol || '$'}{txn.amount}</span>
                      <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                        txn.status === 'completed' ? 'bg-emerald-500/10 text-emerald-600' :
                        txn.status === 'failed' ? 'bg-destructive/10 text-destructive' :
                        'bg-amber-500/10 text-amber-600'
                      }`}>{txn.status}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      {txn.from_name && <span>{txn.from_name} → {txn.to_name || 'Platform'}</span>}
                      {txn.org_name && <span className="inline-flex items-center gap-1"><BuildingIcon className="size-3" />{txn.org_name}</span>}
                      <span className="inline-flex items-center gap-1"><ClockIcon className="size-3" />{formatTimestamp(txn.timestamp)}</span>
                    </div>
                  </div>
                  <HashIcon className="mt-1 size-3 shrink-0 text-muted-foreground/50" />
                  <span className="text-[10px] text-muted-foreground/50">#{txn.id}</span>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="gap-1.5"><ChevronLeftIcon className="size-4" />Previous</Button>
          <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="gap-1.5">Next<ChevronRightIcon className="size-4" /></Button>
        </div>
      )}
    </div>
  )
}

function CyclesView() {
  const [cycles, setCycles] = useState<AdminCycle[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [limit] = useState(50)
  const [loading, setLoading] = useState(true)

  const [statusFilter, setStatusFilter] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  const [forcePayCycleId, setForcePayCycleId] = useState<number | null>(null)
  const [forcePaying, setForcePaying] = useState(false)
  const [confirmForcePay, setConfirmForcePay] = useState<number | null>(null)

  const totalPages = Math.max(1, Math.ceil(total / limit))

  const buildParams = useCallback(() => {
    const params: Record<string, string | number> = { page, limit }
    if (statusFilter) params.status = statusFilter
    return params
  }, [page, limit, statusFilter])

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const res = await getAdminCyclesApi(buildParams())
        if (!cancelled) { setCycles(res.data); setTotal(res.total) }
      } catch (err) {
        if (!cancelled) toast.error(err instanceof Error ? err.message : 'Failed to load cycles')
      } finally { if (!cancelled) setLoading(false) }
    }
    load()
    return () => { cancelled = true }
  }, [buildParams])

  const hasFilters = !!statusFilter

  const handleForcePay = async (cycleId: number) => {
    setForcePayCycleId(cycleId)
    setForcePaying(true)
    setConfirmForcePay(null)
    try {
      const res = await forcePayCycleApi(cycleId)
      toast.success(res.message)
      setCycles((prev) =>
        prev.map((c) =>
          c.payment_cycle_id === cycleId ? { ...c, status: 'paid' } : c,
        ),
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to force-pay cycle')
    } finally {
      setForcePayCycleId(null)
      setForcePaying(false)
    }
  }

  const clearFilters = () => { setStatusFilter(''); setPage(1) }

  if (loading && cycles.length === 0) {
    return (
      <div>
        <SkeletonCard count={5} />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-1.5">
          <FilterIcon className="size-4" />{showFilters ? 'Hide filters' : 'Filters'}
          {hasFilters && <span className="flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">!</span>}
        </Button>
        {hasFilters && <Button variant="outline" size="sm" onClick={clearFilters} className="gap-1.5"><XIcon className="size-4" />Clear</Button>}
      </div>

      {showFilters && (
        <div className="mb-4 rounded-xl border border-border bg-card p-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary">
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
          </div>
        </div>
      )}

      <p className="mb-4 text-xs text-muted-foreground">{total} cycle{total !== 1 ? 's' : ''}{page > 1 && ` — page ${page} of ${totalPages}`}</p>

      {cycles.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
          <RefreshCwIcon className="size-10 text-muted-foreground/40" />
          <p className="text-sm font-medium text-foreground">No payment cycles found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {cycles.map((cycle) => {
            const statusInfo = CYCLE_STATUS_STYLES[cycle.status] ?? { label: cycle.status, color: 'bg-muted text-muted-foreground' }
            const canForcePay = cycle.status === 'overdue' || cycle.status === 'pending'
            return (
              <div key={cycle.payment_cycle_id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-start gap-4">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                    {cycle.status === 'paid' ? <CheckCircleIcon className="size-4 text-emerald-500" /> : cycle.status === 'overdue' ? <AlertTriangleIcon className="size-4 text-destructive" /> : <ClockIcon className="size-4 text-amber-500" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-foreground">Cycle #{cycle.cycle_number}</span>
                      <span className="text-muted-foreground/50">·</span>
                      <span className="text-muted-foreground">{cycle.org_name || 'Unknown org'}</span>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span className="tabular-nums">{cycle.currency_symbol || '$'}{cycle.amount_per_person}/person</span>
                      <span className="tabular-nums">{cycle.currency_symbol || '$'}{cycle.total_expected} total</span>
                      <span className="inline-flex items-center gap-1"><UsersIcon className="size-3" />{cycle.participant_count} participants</span>
                      <span className="inline-flex items-center gap-1"><ClockIcon className="size-3" />Due {formatDate(cycle.due_date)}</span>
                    </div>

                    {/* Force-pay button for overdue/pending cycles */}
                    {canForcePay && (
                      <div className="mt-3 flex justify-end border-t border-border pt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setConfirmForcePay(cycle.payment_cycle_id)}
                          disabled={forcePayCycleId === cycle.payment_cycle_id}
                          className="gap-1.5 border-amber-500/30 text-amber-600 hover:bg-amber-500/10"
                        >
                          {forcePayCycleId === cycle.payment_cycle_id ? (
                            <LoaderIcon className="size-4 animate-spin" />
                          ) : (
                            <ZapIcon className="size-4" />
                          )}
                          Force pay
                        </Button>
                      </div>
                    )}
                  </div>
                  <HashIcon className="mt-1 size-3 shrink-0 text-muted-foreground/50" />
                  <span className="text-[10px] text-muted-foreground/50">#{cycle.payment_cycle_id}</span>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="gap-1.5"><ChevronLeftIcon className="size-4" />Previous</Button>
          <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="gap-1.5">Next<ChevronRightIcon className="size-4" /></Button>
        </div>
      )}

      {/* Force-pay confirmation dialog */}
      <DestructiveConfirm
        open={confirmForcePay !== null}
        onClose={() => setConfirmForcePay(null)}
        title="Force-pay payment cycle?"
        description={`This will mark cycle #${confirmForcePay} as paid and settle all outstanding payer obligations. Only use this for stuck or verification-deadlocked cycles. This override bypasses normal payment verification.`}
        confirmLabel="Yes, force pay"
        onConfirm={() => {
          if (confirmForcePay !== null) handleForcePay(confirmForcePay)
        }}
        loading={forcePaying}
      />
    </div>
  )
}
